"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import Script from "next/script";
import { isStagingDomain } from "@/utils/urls";
import isLocalDomain from "@/utils/urls/isLocalDomain";
import { usePathname, useSearchParams } from "next/navigation";
import { getGoogleSignInReturnPath } from "@/utils/helpers";
import { isMobile } from "react-device-detect";
import { useAuth } from "@/react-query/queries";
import Cookies from "js-cookie";
import { GOOGLE_ONE_TAP_CONSENT_GRANTED_EVENT, GOOGLE_ONE_TAP_SCRIPT_SRC } from "@/components/Tracking/GoogleOneTapConsentBridge";
interface GoogleSignInScriptBaseProps {
  enabled: boolean;
  deferScriptUntilPiwikConsent: boolean;
  disabledPathRules?: Array<{
    prefix: string;
    segmentCount: number;
  }>;
}
const localClientId = "1060630107119-l5obgeu0em8ctltqi11c9bvv8feju7t1.apps.googleusercontent.com";
const stagingClientId = "1060630107119-u0p6vuvpgomsu6a73vcavltcnubnf2io.apps.googleusercontent.com";
const productionClientId = "1060630107119-oife0rmktfv22vavj00be75nm3koeh8q.apps.googleusercontent.com";
export default function GoogleSignInScriptBase(t0) {
  const $ = _c(20);
  const {
    enabled,
    deferScriptUntilPiwikConsent,
    disabledPathRules: t1
  } = t0;
  const disabledPathRules = t1 === undefined ? [] : t1;
  const [origin, setOrigin] = useState("");
  const [hostname, setHostname] = useState("");
  const [clientId, setClientId] = useState(productionClientId);
  const [isReady, setIsReady] = useState(false);
  const [hasPiwikConsentForGoogleOneTap, setHasPiwikConsentForGoogleOneTap] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    data: authData,
    isLoading: isAuthLoading,
    isError: isAuthError
  } = useAuth();
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (typeof window !== "undefined") {
        setOrigin(window.location.origin);
        setHostname(window.location.hostname);
        if (isLocalDomain()) {
          setClientId(localClientId);
        } else {
          if (isStagingDomain()) {
            setClientId(stagingClientId);
          }
        }
        setIsReady(true);
      }
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[2] !== deferScriptUntilPiwikConsent) {
    t4 = () => {
      if (!deferScriptUntilPiwikConsent) {
        return;
      }
      const markGoogleOneTapConsentGranted = () => {
        setHasPiwikConsentForGoogleOneTap(true);
      };
      if (window.GoogleOneTap?.isEnabled?.()) {
        markGoogleOneTapConsentGranted();
        return;
      }
      window.addEventListener(GOOGLE_ONE_TAP_CONSENT_GRANTED_EVENT, markGoogleOneTapConsentGranted);
      const unsubscribeFromBridge = window.GoogleOneTap?.subscribe?.(markGoogleOneTapConsentGranted);
      return () => {
        window.removeEventListener(GOOGLE_ONE_TAP_CONSENT_GRANTED_EVENT, markGoogleOneTapConsentGranted);
        unsubscribeFromBridge?.();
      };
    };
    t5 = [deferScriptUntilPiwikConsent];
    $[2] = deferScriptUntilPiwikConsent;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  const isDisabledForPath = disabledPathRules.some(t6 => {
    const {
      prefix,
      segmentCount
    } = t6;
    if (pathname === prefix) {
      return segmentCount === 0;
    }
    if (!pathname?.startsWith(`${prefix}/`)) {
      return false;
    }
    const remainingPath = pathname.slice(prefix.length);
    const segments = remainingPath.split("/").filter(Boolean);
    return segments.length === segmentCount;
  });
  if (!enabled || isDisabledForPath) {
    return null;
  }
  if (isAuthLoading) {
    return null;
  }
  if (!isAuthError && authData?.logged_in) {
    return null;
  }
  let t7;
  if ($[5] !== pathname || $[6] !== searchParams) {
    t7 = getGoogleSignInReturnPath(pathname, searchParams);
    $[5] = pathname;
    $[6] = searchParams;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const defaultReturnPath = t7;
  const signupHook = isMobile ? "gsso_widget_mobile" : "gsso_widget_desktop";
  const eemailParam = searchParams?.get("eemail");
  const eemailCookie = Cookies.get("_eemail_login");
  const hasEemail = eemailParam || eemailCookie;
  const loginUri = hasEemail ? `${origin}/career/api/google_sso/google_sign_in` : `${origin}/career/api/google_sso/oauth_callback`;
  const shouldLoadGoogleOneTapScript = !deferScriptUntilPiwikConsent || hasPiwikConsentForGoogleOneTap;
  let t8;
  if ($[8] !== shouldLoadGoogleOneTapScript) {
    t8 = shouldLoadGoogleOneTapScript && <Script src={GOOGLE_ONE_TAP_SCRIPT_SRC} strategy="lazyOnload" async={true} defer={true} />;
    $[8] = shouldLoadGoogleOneTapScript;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== clientId || $[11] !== defaultReturnPath || $[12] !== hostname || $[13] !== isReady || $[14] !== loginUri || $[15] !== origin) {
    t9 = isReady && origin && hostname && clientId && <div id="g_id_onload" data-client_id={clientId} data-context="use" data-ux_mode="popup" data-state_cookie_domain={hostname} data-signup_hook={signupHook} data-return_path={defaultReturnPath} data-login_uri={loginUri} />;
    $[10] = clientId;
    $[11] = defaultReturnPath;
    $[12] = hostname;
    $[13] = isReady;
    $[14] = loginUri;
    $[15] = origin;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8 || $[18] !== t9) {
    t10 = <>{t8}{t9}</>;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}